import {Dexie} from 'dexie';
import {
    IVanillaReaderContentPositionData,
    IVanillaReaderPublicationCacheData,
    IVanillaReaderPublicationData,
    IVanillaReaderPublicationOptionsData,
    IVanillaReaderPublicationStateData,
    VanillaPublicationCacheDataCallback,
    VanillaPublicationDataCallback,
    VanillaPublicationOptionsDataCallback,
    VanillaPublicationStateDataCallback,
    VanillaStringValueCallback,
    VanillaVoidCallback,
} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderPublicationDataStore} from './IVanillaReaderPublicationDataStore';
import {
    IVanillaReaderStoreContentPositionData,
    IVanillaReaderStorePublicationCacheData, IVanillaReaderStorePublicationData,
    IVanillaReaderStorePublicationOptionsData
} from "./VanillaReaderStoreModel";
import {
    VanillaReaderPublicationIndexedDbDataStoreConversions
} from "./VanillaReaderPublicationIndexedDbDataStoreConversions";

/*
* # VanillaReaderIndexedDbDataStore
*
* ## RESPONSIBILITIES
*
* This is an implementation of the `IVanillaReaderDataStore` interface that uses IndexedDb. IndexedDb is the preferred
* way to store web app data, However since its APIs are asynchronous it is not possible to use to store state when
* the application is closed by the system, or when it is put in the background on mobile devices.
*
* I suggest using it in combination with the `VanillaReaderLocalStorageDataStore` to get both a fail-safe temp storage,
* and a long-term storage.
*
* Checkout the `IVanillaReaderDataStore` interface documentation for more general information.
*
* */

export class VanillaReaderPublicationIndexedDbDataStore extends Dexie implements IVanillaReaderPublicationDataStore {

    private _vanillaReaderPublicationCacheData: Dexie.Table<IVanillaReaderStorePublicationCacheData, string>;
    private _vanillaReaderPublicationOptionsData: Dexie.Table<IVanillaReaderStorePublicationOptionsData, string>;
    private _vanillaReaderPublicationData: Dexie.Table<IVanillaReaderStorePublicationData, string>;
    private _vanillaReaderPublicationReadingPositionData: Dexie.Table<IVanillaReaderStoreContentPositionData, string>;

    private _onPublicationDataAdded: VanillaPublicationStateDataCallback | undefined;
    private _onPublicationDataUpdated: VanillaPublicationStateDataCallback | undefined;
    private _onPublicationDataDeleted: VanillaStringValueCallback | undefined;

    private _onPublicationCacheDataAdded: VanillaPublicationCacheDataCallback | undefined;
    private _onPublicationCacheDataUpdated: VanillaPublicationCacheDataCallback | undefined;
    private _onPublicationCacheDataDeleted: VanillaStringValueCallback | undefined;

    private _onPublicationOptionsDataAdded: VanillaPublicationOptionsDataCallback | undefined;
    private _onPublicationOptionsDataUpdated: VanillaPublicationOptionsDataCallback | undefined;
    private _onPublicationOptionsDataDeleted: VanillaVoidCallback | undefined;

    private _initialized: boolean = false;

    constructor(dbName: string = 'publication.vanillareader.colibrio.com', persist: boolean = true) {
        super(dbName);

        if (persist && navigator.storage && navigator.storage.persist) {
            navigator.storage.persist().then((canPersist) => {
                if (canPersist) {
                    console.log('VanillaReaderPublicationIndexedDbDataStore will persist data.');
                } else {
                    console.log('VanillaReaderPublicationIndexedDbDataStore will NOT persist data.');
                }
            }).catch(console.warn);
        }

        this.version(1).stores({
            vanillaReaderPublicationData: 'id, fileName, url',
            vanillaReaderPublicationOptionsData: 'publicationId',
            vanillaReaderPublicationCacheData: 'publicationId',
            vanillaReaderPublicationReadingPositionData: 'publicationId',
        });

        this._vanillaReaderPublicationData = this.table('vanillaReaderPublicationData');
        this._vanillaReaderPublicationOptionsData = this.table('vanillaReaderPublicationOptionsData');
        this._vanillaReaderPublicationCacheData = this.table('vanillaReaderPublicationCacheData');
        this._vanillaReaderPublicationReadingPositionData = this.table('vanillaReaderPublicationReadingPositionData');
    }

    initialize(): Promise<void> {
        return this.open().then(_db => {
            this._initialized = true
        }).catch(_ => {
            this._initialized = false
        });
    }

    /**
     *
     * IVanillaReaderPublicationMetadata
     *
     * */

    async hasPublicationData(publicationId: string): Promise<boolean> {
        if (this._initialized) {
            return await this._vanillaReaderPublicationData.where('id').equals(publicationId).count() > 0;
        } else {
            return false;
        }
    }

    async hasPublicationDataByFileName(fileName: string): Promise<boolean> {
        if (this._initialized) {
            return await this._vanillaReaderPublicationData.where('fileName').equals(fileName).count() > 0;
        } else {
            return false;
        }
    }

    fetchAllPublicationData(): Promise<IVanillaReaderPublicationData[]> {
        if (this._initialized) {
            return this._vanillaReaderPublicationData.toArray()
                .then(arr => arr.map(VanillaReaderPublicationIndexedDbDataStoreConversions.toApplicationPublicationData));
        } else {
            return Promise.resolve<IVanillaReaderPublicationData[]>([]);
        }
    }

    async fetchPublicationDataByFileName(fileName: string): Promise<IVanillaReaderPublicationStateData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderPublicationData.where('fileName').equals(fileName).first()
                .then(VanillaReaderPublicationIndexedDbDataStoreConversions.maybeToApplicationPublicationData);
        } else {
            return;
        }
    }

    async setPublicationDataByFileName(
        fileName: string,
        state: IVanillaReaderPublicationData,
    ): Promise<void> {
        if (this._initialized) {
            // There can be only one!
            await this.deletePublicationDataByFileName(fileName);
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationData(state);
            await this._vanillaReaderPublicationData.add(storeState);

            if (this._onPublicationDataAdded) {
                this._onPublicationDataAdded(state);
            }
        }
        return Promise.resolve();
    }

    async updatePublicationDataByFileName(
        fileName: string,
        state: IVanillaReaderPublicationStateData,
    ): Promise<void> {
        if (this._initialized) {
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationData(state);
            await this._vanillaReaderPublicationData.where('fileName').equals(fileName).modify(storeState);

            if (this._onPublicationDataUpdated) {
                this._onPublicationDataUpdated(state);
            }
        }
        return Promise.resolve(undefined);
    }

    async deletePublicationDataByFileName(fileName: string): Promise<number> {
        if (this._initialized) {
            let numberOfDeletions = this._vanillaReaderPublicationData.where('fileName').equals(fileName).delete();

            if (this._onPublicationDataDeleted) {
                this._onPublicationDataDeleted(fileName);
            }

            return Promise.resolve(numberOfDeletions);
        } else {
            return 0;
        }

    }

    async fetchPublicationData(publicationId: string): Promise<IVanillaReaderPublicationData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderPublicationData.where('id').equals(publicationId).first()
                .then(VanillaReaderPublicationIndexedDbDataStoreConversions.maybeToApplicationPublicationData);
        } else {
            return;
        }
    }

    async setPublicationData(
        publicationId: string,
        state: IVanillaReaderPublicationData,
    ): Promise<void> {
        if (this._initialized) {
            // There can be only one!
            await this._vanillaReaderPublicationData.where('id').equals(publicationId).delete();
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationData(state);
            await this._vanillaReaderPublicationData.add(storeState);

            if (this._onPublicationDataAdded) {
                this._onPublicationDataAdded(state);
            }
        }
        return Promise.resolve();
    }

    async deletePublicationData(publicationId: string): Promise<number> {
        if (this._initialized) {
            let numberOfDeletions = this._vanillaReaderPublicationData.where('id').equals(publicationId).delete();

            if (this._onPublicationDataDeleted) {
                this._onPublicationDataDeleted(publicationId);
            }

            return Promise.resolve(numberOfDeletions);
        } else {
            return 0;
        }
    }

    async updatePublicationData(
        publicationId: string,
        state: IVanillaReaderPublicationStateData,
    ): Promise<void> {
        if (this._initialized) {
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationData(state);
            await this._vanillaReaderPublicationData.where('id').equals(publicationId).modify(storeState);

            if (this._onPublicationDataUpdated) {
                this._onPublicationDataUpdated(state);
            }
        }
        return;
    }

    /**
     *
     * IVanillaReaderPublicationCacheData
     *
     * */

    async hasPublicationCacheData(publicationId: string): Promise<boolean> {
        if (this._initialized) {
            return await this._vanillaReaderPublicationCacheData.where('publicationId').equals(publicationId).count() > 0;
        } else {
            return false;
        }
    }

    async fetchPublicationCacheData(publicationId: string): Promise<IVanillaReaderPublicationCacheData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderPublicationCacheData.where('publicationId').equals(publicationId).first()
                .then(VanillaReaderPublicationIndexedDbDataStoreConversions.maybeToApplicationPublicationCacheData);
        } else {
            return;
        }
    }

    async setPublicationCacheData(
        publicationId: string,
        state: IVanillaReaderPublicationCacheData,
    ): Promise<void> {
        if (this._initialized) {
            // There can be only one!
            await this._vanillaReaderPublicationCacheData.where('publicationId').equals(publicationId).delete();
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationCacheData(state);
            await this._vanillaReaderPublicationCacheData.add(storeState, publicationId);

            if (this._onPublicationCacheDataAdded) {
                this._onPublicationCacheDataAdded(state);
            }
        }
        return Promise.resolve();
    }

    async updatePublicationCacheData(publicationId: string, state: IVanillaReaderPublicationCacheData): Promise<void> {
        if (this._initialized) {
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationCacheData(state);
            await this._vanillaReaderPublicationCacheData.update(publicationId, storeState);

            if (this._onPublicationCacheDataUpdated) {
                this._onPublicationCacheDataUpdated(state);
            }
        }
        return Promise.resolve();
    }

    async deletePublicationCacheData(publicationId: string): Promise<number> {
        if (this._initialized) {
            let numberOfDeletions: number = await this._vanillaReaderPublicationCacheData.where('publicationId').equals(publicationId).delete();

            if (this._onPublicationCacheDataDeleted) {
                this._onPublicationCacheDataDeleted(publicationId);
            }

            return Promise.resolve(numberOfDeletions);
        } else {
            return 0;
        }
    }

    /**
     *
     * IVanillaReaderPublicationOptionsData
     *
     * */

    async hasPublicationOptionsData(publicationId: string): Promise<boolean> {
        if (this._initialized) {
            return await this._vanillaReaderPublicationOptionsData.where('publicationId').equals(publicationId).count() > 0;
        } else {
            return false;
        }
    }

    async deletePublicationOptionsData(publicationId: string): Promise<number> {
        if (this._initialized) {
            let numberOfDeletions: number = await this._vanillaReaderPublicationOptionsData.where('publicationId').equals(publicationId).delete();

            if (this._onPublicationOptionsDataDeleted) {
                this._onPublicationOptionsDataDeleted();
            }

            return Promise.resolve(numberOfDeletions);
        } else {
            return 0;
        }

    }

    fetchAllPublicationOptionsData(): Promise<IVanillaReaderPublicationOptionsData[]> {
        if (this._initialized) {
            return this._vanillaReaderPublicationOptionsData.toArray()
                .then(arr => arr.map(VanillaReaderPublicationIndexedDbDataStoreConversions.toApplicationPublicationOptionsData));
        } else {
            return Promise.resolve([]);
        }
    }

    fetchPublicationOptionsData(publicationId: string): Promise<IVanillaReaderPublicationOptionsData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderPublicationOptionsData.where('publicationId').equals(publicationId).first()
                .then(VanillaReaderPublicationIndexedDbDataStoreConversions.maybeToApplicationPublicationOptionsData);
        } else {
            return Promise.resolve(undefined);
        }
    }

    async setPublicationOptionsData(publicationId: string, state: IVanillaReaderPublicationOptionsData): Promise<void> {
        if (this._initialized) {
            await this._vanillaReaderPublicationOptionsData.where('publicationId').equals(publicationId).delete();
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationOptionsData(state);
            await this._vanillaReaderPublicationOptionsData.add(storeState);

            if (this._onPublicationOptionsDataAdded) {
                this._onPublicationOptionsDataAdded(state);
            }
        }
        return Promise.resolve();
    }

    async updatePublicationOptionsData(
        publicationId: string,
        state: IVanillaReaderPublicationOptionsData,
    ): Promise<void> {
        if (this._initialized) {
            if (!await this.hasPublicationOptionsData(publicationId)) {
                await this.setPublicationOptionsData(publicationId, {
                    publicationId,
                })
            }

            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStorePublicationOptionsData(state);
            await this._vanillaReaderPublicationOptionsData.update(publicationId, storeState);

            if (this._onPublicationOptionsDataUpdated) {
                this._onPublicationOptionsDataUpdated(state);
            }
        }
        return Promise.resolve();
    }

    /**
     *
     * IVanillaReaderReadingPositionData
     *
     * */

    async hasPublicationReadingPositionData(publicationId: string): Promise<boolean> {
        if (this._initialized) {
            return await this._vanillaReaderPublicationReadingPositionData.where('publicationId').equals(publicationId).count() > 0;
        } else {
            return false;
        }
    }

    async setPublicationReadingPositionData(publicationId: string, state: IVanillaReaderContentPositionData): Promise<void> {
        if (this._initialized) {
            const storeState = VanillaReaderPublicationIndexedDbDataStoreConversions.toStoreContentPositionData(state);
            await this._vanillaReaderPublicationReadingPositionData.put(storeState, publicationId);
            return Promise.resolve();
        } else {
            return;
        }
    }

    async fetchPublicationReadingPositionData(publicationId: string): Promise<IVanillaReaderContentPositionData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderPublicationReadingPositionData.get(publicationId)
                .then(VanillaReaderPublicationIndexedDbDataStoreConversions.maybeToApplicationContentPositionData);
        } else {
            return;
        }
    }

    async clearPublicationReadingPositionData(publicationId: string): Promise<void> {
        if (this._initialized) {
            return this._vanillaReaderPublicationReadingPositionData.delete(publicationId);
        }
    }

    /**
     *
     * IVanillaReaderPublicationStateData
     *
     * */


    async fetchCompletePublicationStateData(publicationId: string): Promise<IVanillaReaderPublicationStateData> {
        let publicationMetadataData = await this.fetchPublicationData(publicationId);
        let publicationOptionsData = await this.fetchPublicationOptionsData(publicationId);
        let publicationCacheData = await this.fetchPublicationCacheData(publicationId);
        let publicationReadingPositionData = await this.fetchPublicationReadingPositionData(publicationId);

        return this._serializeStateData(publicationMetadataData, publicationOptionsData, publicationCacheData, publicationReadingPositionData);

    }

    async fetchCompletePublicationStateDataByFileName(fileName: string): Promise<IVanillaReaderPublicationStateData> {
        let publicationOptionsData: IVanillaReaderPublicationOptionsData | undefined;
        let publicationCacheData: IVanillaReaderPublicationCacheData | undefined;
        let publicationReadingPositionData: IVanillaReaderContentPositionData | undefined;
        let publicationData = await this.fetchPublicationDataByFileName(fileName);

        if (publicationData && publicationData.id) {
            publicationReadingPositionData = await this.fetchPublicationReadingPositionData(publicationData.id);
            publicationOptionsData = await this.fetchPublicationOptionsData(publicationData.id);
            publicationCacheData = await this.fetchPublicationCacheData(publicationData.id);
        }

        return this._serializeStateData(publicationData, publicationOptionsData, publicationCacheData, publicationReadingPositionData);

    }

    async setCompletePublicationStateData(stateData: IVanillaReaderPublicationStateData): Promise<void> {

        if (!stateData.id) {
            console.error('VanillaReaderPublicationIndexedDbDataStore.setCompletePublicationStateData(): No valid id parameter found in state data.');
            return;
        }

        const publicationData: IVanillaReaderPublicationData = {
            id: stateData?.id,
            fileName: stateData?.fileName || '',
            metadata: stateData?.metadata,
            url: stateData?.url || '',
            isRtl: stateData?.isRtl,
            isAudiobook: stateData?.isAudiobook,
            format: stateData?.format || '',
            title: stateData?.title,
            coverImageAsBase64: stateData?.coverImageAsBase64,
            hasMoSyncMedia: stateData?.hasMoSyncMedia,
            isScripted: stateData?.isScripted,
            isFixedLayout: stateData?.isFixedLayout,
            fileSourceUri: stateData?.fileSourceUri || '',
        };

        this.setPublicationData(stateData.id, publicationData).catch(console.warn);

        const publicationCacheData: IVanillaReaderPublicationCacheData = {
            publicationId: stateData.id,
            searchIndexData: stateData?.searchIndexData,
            navigationTreeData: stateData?.navigationTreeData,
            landmarkData: stateData?.landmarkData,
            timelineData: stateData?.timelineData,
            storageState: stateData?.storageState,
        };

        this.setPublicationCacheData(stateData.id, publicationCacheData).catch(console.warn);

        const publicationOptionsData: IVanillaReaderPublicationOptionsData = {
            publicationId: stateData.id,
            styleOptions: stateData.styleOptions,
            ttsOptions: stateData?.ttsOptions,
        };

        this.setPublicationOptionsData(stateData.id, publicationOptionsData).catch(console.warn);

        if (stateData.latestReadingPositionData) {
            this.setPublicationReadingPositionData(stateData.id, stateData.latestReadingPositionData).catch(console.warn);
        }
    }

    async _serializeStateData(
        publicationData: IVanillaReaderPublicationData | undefined,
        publicationOptionsData: IVanillaReaderPublicationOptionsData | undefined,
        publicationCacheData: IVanillaReaderPublicationCacheData | undefined,
        publicationReadingPositionData: IVanillaReaderContentPositionData | undefined,
    ): Promise<IVanillaReaderPublicationStateData> {

        return {
            id: publicationData?.id || '',
            fileName: publicationData?.fileName || '',
            metadata: publicationData?.metadata,
            url: publicationData?.url || '',
            isRtl: publicationData?.isRtl,
            isAudiobook: publicationData?.isAudiobook,
            format: publicationData?.format || '',
            title: publicationData?.title,
            coverImageAsBase64: publicationData?.coverImageAsBase64,
            hasMoSyncMedia: publicationData?.hasMoSyncMedia,
            isScripted: publicationData?.isScripted,
            isFixedLayout: publicationData?.isFixedLayout,
            fileSourceUri: publicationData?.fileSourceUri || '',

            searchIndexData: publicationCacheData?.searchIndexData,
            navigationTreeData: publicationCacheData?.navigationTreeData,
            landmarkData: publicationCacheData?.landmarkData,
            timelineData: publicationCacheData?.timelineData,
            storageState: publicationCacheData?.storageState,

            latestReadingPositionData: publicationReadingPositionData,

            styleOptions: publicationOptionsData?.styleOptions,
            ttsOptions: publicationOptionsData?.ttsOptions,
        } ;
    }

    /**
     *
     * IVanillaReaderPublicationMetadata
     *
     * */


    onPublicationDataAdded(callback: VanillaPublicationDataCallback): void {
        this._onPublicationDataAdded = callback;
    }

    onPublicationDataDeleted(callback: VanillaStringValueCallback): void {
        this._onPublicationDataDeleted = callback;
    }

    onPublicationDataUpdated(callback: VanillaPublicationStateDataCallback): void {
        this._onPublicationDataUpdated = callback;
    }

    /**
     *
     * IVanillaReaderPublicationCacheData
     *
     * */

    onPublicationCacheDataUpdated(callback: VanillaPublicationCacheDataCallback): void {
        this._onPublicationCacheDataUpdated = callback;
    }

    onPublicationCacheDataAdded(callback: VanillaPublicationCacheDataCallback): void {
        this._onPublicationCacheDataAdded = callback;
    }

    onPublicationCacheDataDeleted(callback: VanillaVoidCallback): void {
        this._onPublicationOptionsDataDeleted = callback;
    }

    /**
     *
     * IVanillaReaderPublicationOptionsData
     *
     * */

    onPublicationOptionsDataUpdated(callback: VanillaPublicationOptionsDataCallback): void {
        this._onPublicationOptionsDataUpdated = callback;
    }

    onPublicationOptionsDataAdded(callback: VanillaPublicationOptionsDataCallback): void {
        this._onPublicationOptionsDataAdded = callback;
    }

    onPublicationOptionsDataCleared(callback: VanillaVoidCallback): void {
        this._onPublicationOptionsDataDeleted = callback;
    }

}
